import { getMeta, setMeta } from './db.ts';
import type { RemotePrefs } from './api.ts';

/**
 * Preferences that follow the account to every device: the look of the app and which
 * notifications to send. Stored locally first (the app must look right offline, on the first
 * frame) and synced as one blob, last write wins — the server reads `notify` to decide whom to
 * notify, so that part of the shape is a contract with the backend.
 */

export type ThemeMode = 'auto' | 'light' | 'dark';

export const ACCENTS = {
  indaco: { label: 'Indaco', hex: '#4f46e5' },
  ottanio: { label: 'Ottanio', hex: '#0f766e' },
  mirtillo: { label: 'Mirtillo', hex: '#7c3aed' },
  rubino: { label: 'Rubino', hex: '#be123c' },
  ambra: { label: 'Ambra', hex: '#b45309' },
  ardesia: { label: 'Ardesia', hex: '#334155' },
} as const;

export type Accent = keyof typeof ACCENTS;

export interface Prefs {
  theme: { mode: ThemeMode; accent: Accent };
  notify: { expiry: boolean; news: boolean };
}

export const DEFAULT_PREFS: Prefs = {
  theme: { mode: 'auto', accent: 'indaco' },
  notify: { expiry: true, news: true },
};

interface StoredPrefs {
  data: Prefs;
  updatedAt: number;
  dirty: 0 | 1;
}

const CACHE_KEY = 'klarnus.prefs';

/** Fills gaps and drops nonsense, so a blob from an older or newer app version is always usable. */
export function normalizePrefs(raw: unknown): Prefs {
  const r = (raw ?? {}) as { theme?: Partial<Prefs['theme']>; notify?: Partial<Prefs['notify']> };
  const mode = r.theme?.mode;
  const accent = r.theme?.accent;
  return {
    theme: {
      mode: mode === 'light' || mode === 'dark' || mode === 'auto' ? mode : DEFAULT_PREFS.theme.mode,
      accent: accent && accent in ACCENTS ? accent : DEFAULT_PREFS.theme.accent,
    },
    notify: {
      expiry: r.notify?.expiry !== false,
      news: r.notify?.news !== false,
    },
  };
}

/**
 * The theme has to be on screen before React renders, or every launch flashes the wrong colours.
 * IndexedDB is asynchronous, so a copy also lives in localStorage, which is not.
 */
export function cachedPrefs(): Prefs {
  try {
    return normalizePrefs(JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null'));
  } catch {
    return DEFAULT_PREFS;
  }
}

function cache(prefs: Prefs): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(prefs));
  } catch {
    // Storage blocked: the theme falls back to automatic on the next launch, nothing worse.
  }
}

export async function loadPrefs(): Promise<Prefs> {
  const stored = await getMeta<StoredPrefs | null>('prefs', null);
  return stored ? normalizePrefs(stored.data) : cachedPrefs();
}

type Listener = (prefs: Prefs) => void;
const listeners = new Set<Listener>();

export function onPrefsChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function publish(prefs: Prefs): void {
  cache(prefs);
  applyTheme(prefs);
  for (const l of listeners) l(prefs);
}

/** A change made on this device: saved now, pushed with the next sync. */
export async function updatePrefs(patch: { theme?: Partial<Prefs['theme']>; notify?: Partial<Prefs['notify']> }): Promise<Prefs> {
  const current = await loadPrefs();
  const next = normalizePrefs({
    theme: { ...current.theme, ...patch.theme },
    notify: { ...current.notify, ...patch.notify },
  });
  await setMeta('prefs', { data: next, updatedAt: Date.now(), dirty: 1 } satisfies StoredPrefs);
  publish(next);
  return next;
}

/** What the next sync should send, if anything changed here. */
export async function dirtyPrefs(): Promise<RemotePrefs | null> {
  const stored = await getMeta<StoredPrefs | null>('prefs', null);
  return stored?.dirty ? { data: stored.data as unknown as Record<string, unknown>, updatedAt: stored.updatedAt } : null;
}

export async function markPrefsClean(updatedAt: number): Promise<void> {
  const stored = await getMeta<StoredPrefs | null>('prefs', null);
  if (stored && stored.updatedAt === updatedAt) await setMeta('prefs', { ...stored, dirty: 0 });
}

/** A newer blob from the server (another device changed it) replaces the local one. */
export async function takeRemotePrefs(remote: RemotePrefs): Promise<void> {
  const stored = await getMeta<StoredPrefs | null>('prefs', null);
  if (stored && (stored.dirty || stored.updatedAt >= remote.updatedAt)) return;
  const data = normalizePrefs(remote.data);
  await setMeta('prefs', { data, updatedAt: remote.updatedAt, dirty: 0 } satisfies StoredPrefs);
  publish(data);
}

/** Colour of the browser chrome and the status bar, matched to the ground of the app. */
const GROUND = { light: '#eef1f4', dark: '#0f151c' };

export function resolvedMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'auto') return mode;
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(prefs: Prefs): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = prefs.theme.mode;
  root.style.setProperty('--accent', ACCENTS[prefs.theme.accent].hex);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', GROUND[resolvedMode(prefs.theme.mode)]);
}
